// Bảo mật tài khoản cá nhân
// - Admin: KHÔNG self-setup L1 (admin setup lẫn nhau hoặc qua seed). Chỉ đổi mật khẩu.
// - NV:    Self-setup TOTP L2 (AC-AUTH-3). L1 do admin tạo, không tự làm được.
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuthStore } from '../lib/auth.js';

export default function SecurityPage() {
  const { user, setAuth, refreshToken, logout } = useAuthStore();
  const [me, setMe] = useState(null);

  async function reload() {
    try {
      const r = await api.me();
      setMe(r.user);
      // Sync store user (flag totp_l2_enabled có thể đã đổi)
      setAuth({ accessToken: useAuthStore.getState().accessToken, refreshToken, user: r.user });
    } catch {}
  }

  useEffect(() => { reload(); }, []);

  return (
    <div className="min-h-full p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">← Quay lại</Link>

        <div>
          <h1 className="text-2xl font-semibold text-brand-700">Bảo mật tài khoản</h1>
          <p className="text-sm text-slate-500 mt-1">
            {user?.role === 'admin'
              ? 'Đổi mật khẩu. Mã TOTP L1 được admin setup cho nhau — không self-setup.'
              : 'Đổi mật khẩu + thiết lập TOTP L2 (dùng mỗi phiên nhạy cảm).'}
          </p>
        </div>

        <PasswordChangeCard onDone={() => { logout(); }} />

        {user?.role === 'nv' && (
          <L2SetupCard
            enabled={!!(me?.totp_l2_enabled ?? user?.totp_l2_enabled)}
            onChanged={reload}
          />
        )}

        {user?.role === 'admin' && (
          <div className="bg-sky-50 border border-sky-200 rounded-2xl p-5 text-sm text-sky-800">
            ℹ️ Admin dùng TOTP L1. Nếu cần reset, một admin khác mở <Link to="/admin/users" className="underline font-medium">Quản lý người dùng</Link> → chọn account admin → <strong>Tạo/Đặt lại L1</strong>.
          </div>
        )}
      </div>
    </div>
  );
}

function PasswordChangeCard({ onDone }) {
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (nw.length < 8) return toast.error('Mật khẩu mới phải ≥ 8 ký tự');
    setBusy(true);
    try {
      await api.changePassword({ current_password: cur, new_password: nw });
      toast.success('Đổi mật khẩu thành công — đăng nhập lại');
      setCur(''); setNw('');
      onDone?.();
    } catch (err) {
      toast.error(err.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-3">
      <h2 className="font-semibold text-slate-800">Đổi mật khẩu</h2>
      <Field label="Mật khẩu hiện tại" type="password" value={cur} onChange={e => setCur(e.target.value)} />
      <Field label="Mật khẩu mới (≥ 8 ký tự)" type="password" value={nw} onChange={e => setNw(e.target.value)} />
      <button disabled={busy || !cur || nw.length < 8} className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium disabled:opacity-50">
        {busy ? 'Đang đổi…' : 'Đổi mật khẩu'}
      </button>
      <p className="text-xs text-slate-400">Sau khi đổi, hệ thống tự đăng xuất mọi phiên.</p>
    </form>
  );
}

function L2SetupCard({ enabled, onChanged }) {
  const [setup, setSetup] = useState(null); // { qr_png, secret_base32, otpauth_uri }
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  async function begin() {
    setBusy(true);
    try {
      const res = await api.totpL2Setup();
      setSetup(res);
    } catch (err) {
      toast.error(err.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (code.length !== 6) return;
    setBusy(true);
    try {
      await api.totpL2Enable(code);
      toast.success('Đã bật TOTP L2 — dùng mỗi khi thao tác Học / Kiểm tra / Hỏi đáp');
      setSetup(null); setCode('');
      onChanged?.();
    } catch (err) {
      toast.error(err.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-3">
      <div className="flex items-baseline justify-between">
        <h2 className="font-semibold text-slate-800">TOTP L2 (mã lớp 2 cho NV)</h2>
        <span className="text-xs text-slate-500">
          {enabled
            ? <span className="text-emerald-600 font-medium">✅ Đã bật</span>
            : <span>⚪ Chưa bật</span>}
        </span>
      </div>

      <p className="text-sm text-slate-500">
        Dùng app Authenticator trên điện thoại cá nhân (Google Authenticator / Microsoft Authenticator).
        Mỗi phiên L2 kéo dài <strong>4 giờ</strong>; hết hạn sẽ nhập lại 1 mã.
      </p>

      {enabled && !setup && (
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
          L2 đã bật. Nếu mất điện thoại, liên hệ admin dùng chức năng <strong>Reset L2</strong>.
        </div>
      )}

      {!enabled && !setup && (
        <button onClick={begin} disabled={busy} className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium disabled:opacity-50">
          {busy ? 'Đang tạo…' : 'Bắt đầu thiết lập TOTP L2'}
        </button>
      )}

      {setup && (
        <div className="space-y-3 pt-2 border-t border-slate-100">
          <div className="text-sm text-slate-700">Bước 1: Mở app Authenticator → quét QR hoặc nhập chuỗi bên dưới:</div>
          <div className="flex justify-center">
            <img src={setup.qr_png} alt="QR L2" className="border border-slate-200 rounded" />
          </div>
          <div className="text-center font-mono text-xs text-slate-500 select-all break-all">{setup.secret_base32}</div>
          <label className="block">
            <span className="block text-sm font-medium">Bước 2: Nhập mã 6 số hiện tại để xác nhận</span>
            <input
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 font-mono text-center tracking-widest"
              placeholder="000000"
            />
          </label>
          <div className="flex gap-2">
            <button onClick={() => { setSetup(null); setCode(''); }} className="flex-1 py-2 rounded-lg bg-slate-100 text-sm">Huỷ</button>
            <button onClick={confirm} disabled={busy || code.length !== 6} className="flex-1 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium disabled:opacity-50">
              {busy ? 'Đang xác nhận…' : 'Xác nhận & Bật'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, ...rest }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700">{label}</span>
      <input required className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none" {...rest} />
    </label>
  );
}
